import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import {
  Shield, Building2, Users, ChevronRight, LogOut, Eye,
  ToggleLeft, ToggleRight, Activity, ExternalLink, Search,
  Power, Crown, UserPlus, Key, Lock, ShieldCheck, X, Check,
  Trash2, Download, Edit2, Plus, RefreshCw, Globe, ChevronDown
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLAN_COLORS = {
  trial: 'bg-yellow-900/40 text-yellow-300 border-yellow-700/50',
  free: 'bg-gray-800 text-gray-300 border-gray-700',
  pro: 'bg-blue-900/40 text-blue-300 border-blue-700/50',
  business: 'bg-purple-900/40 text-purple-300 border-purple-700/50',
};

const BASE_ROLES = ['admin', 'manager', 'team_lead', 'tier1', 'tier2', 'solution_engineer', 'viewer'];

// Build dynamic role list from org users + base roles
function getOrgRoles(orgUsers) {
  const rolesFromUsers = orgUsers.map(u => u.role).filter(Boolean);
  const allRoles = [...new Set([...BASE_ROLES, ...rolesFromUsers])];
  return allRoles.sort((a, b) => {
    // Keep base roles first in order, then custom roles alphabetically
    const ai = BASE_ROLES.indexOf(a);
    const bi = BASE_ROLES.indexOf(b);
    if (ai >= 0 && bi >= 0) return ai - bi;
    if (ai >= 0) return -1;
    if (bi >= 0) return 1;
    return a.localeCompare(b);
  });
}

export default function PlatformDashboard({ token, admin, onLogout }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [view, setView] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [orgUsers, setOrgUsers] = useState([]);
  const [orgDetail, setOrgDetail] = useState(null);
  const [auditLog, setAuditLog] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [activity, setActivity] = useState([]);

  // Admin management
  const [admins, setAdmins] = useState([]);
  const [showCreateAdmin, setShowCreateAdmin] = useState(false);
  const [newAdmin, setNewAdmin] = useState({ name: '', email: '', password: '' });
  const [creatingAdmin, setCreatingAdmin] = useState(false);

  // Password change
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm: '' });
  const [changingPw, setChangingPw] = useState(false);

  // Create org
  const [showCreateOrg, setShowCreateOrg] = useState(false);
  const [newOrg, setNewOrg] = useState({ name: '', slug: '', plan: 'trial', max_users: 25, admin_name: '', admin_email: '', admin_password: '' });
  const [creatingOrg, setCreatingOrg] = useState(false);

  // Edit org
  const [showEditOrg, setShowEditOrg] = useState(false);
  const [editOrgForm, setEditOrgForm] = useState({});

  // Global user search
  const [globalSearch, setGlobalSearch] = useState('');
  const [globalResults, setGlobalResults] = useState([]);
  const [searching, setSearching] = useState(false);

  // DB Inspector — for diagnosing shared-DB / org_id collision issues.
  const [inspectData, setInspectData] = useState(null);
  const [inspectLoading, setInspectLoading] = useState(false);

  const loadInspect = async () => {
    setInspectLoading(true);
    try {
      const res = await axios.get(`${API}/super-admin/system/orgs-inspect`, { headers });
      setInspectData(res.data);
    } catch (e) {
      toast.error('DB inspect failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setInspectLoading(false);
    }
  };

  // User management modals
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '', role: 'tier1' });
  const [addingUser, setAddingUser] = useState(false);
  const [userAction, setUserAction] = useState(null); // { type: 'reset_password' | 'change_role', user }
  const [actionValue, setActionValue] = useState('');

  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Session expiry
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      res => res,
      err => {
        if (err.response?.status === 401 && err.config?.headers?.Authorization?.includes(token)) {
          toast.error('Session expired. Please log in again.');
          onLogout();
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [token, onLogout]);

  const loadDashboard = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/super-admin/dashboard`, { headers });
      setDashboard(res.data);
    } catch (err) {
      if (err.response?.status !== 401) toast.error('Failed to load dashboard');
    }
    // Activity feed loaded separately so its failure doesn't break dashboard
    try {
      const res = await axios.get(`${API}/super-admin/activity?limit=10`, { headers });
      setActivity(res.data);
    } catch { /* Activity feed is optional */ }
  }, [token]);

  const loadOrgs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/super-admin/orgs`, { headers });
      setOrgs(res.data);
    } catch (err) {
      if (err.response?.status !== 401) toast.error('Failed to load organizations');
    }
  }, [token]);

  const loadAdmins = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/super-admin/admins`, { headers });
      setAdmins(res.data);
    } catch (err) {
      if (err.response?.status === 401) return;
      // Don't show error toast for 502/404 - endpoint may not exist on older backend
      if (err.response?.status !== 502 && err.response?.status !== 404) {
        toast.error('Failed to load admins');
      }
    }
  }, [token]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([loadDashboard(), loadOrgs()]);
      setLoading(false);
    })();
  }, []);

  const loadOrgDetail = async (orgId) => {
    try {
      const [detailRes, usersRes] = await Promise.all([
        axios.get(`${API}/super-admin/orgs/${orgId}`, { headers }),
        axios.get(`${API}/super-admin/orgs/${orgId}/users`, { headers }),
      ]);
      setOrgDetail(detailRes.data);
      setOrgUsers(usersRes.data);
      setSelectedOrg(orgId);
      setView('org-detail');
    } catch (err) { toast.error('Failed to load org details'); }
  };

  const loadAuditLog = async () => {
    try {
      const res = await axios.get(`${API}/super-admin/audit-log?limit=100`, { headers });
      setAuditLog(res.data);
      setView('audit');
    } catch (err) { toast.error('Failed to load audit log'); }
  };

  const handleUpdateOrg = async (orgId, updates) => {
    try {
      await axios.put(`${API}/super-admin/orgs/${orgId}`, updates, { headers });
      toast.success('Organization updated');
      await loadOrgs();
      if (selectedOrg === orgId) await loadOrgDetail(orgId);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update org'); }
  };

  const handleToggleFeature = async (orgId, featureKey, currentFeatures) => {
    const updated = { ...currentFeatures, [featureKey]: !currentFeatures[featureKey] };
    try {
      await axios.put(`${API}/super-admin/orgs/${orgId}/features`, { features: updated }, { headers });
      toast.success(`Feature ${featureKey} ${updated[featureKey] ? 'enabled' : 'disabled'}`);
      await loadOrgDetail(orgId);
    } catch (err) { toast.error('Failed to update feature'); }
  };

  const handleImpersonate = async (orgId, userId) => {
    try {
      const res = await axios.post(`${API}/super-admin/impersonate`, { org_id: orgId, user_id: userId }, { headers });
      const appUrl = process.env.REACT_APP_BACKEND_URL;
      window.open(`${appUrl}/login?impersonate=${res.data.token}`, '_blank');
      toast.success(res.data.message);
    } catch (err) { toast.error(err.response?.data?.detail || 'Impersonation failed'); }
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    setCreatingAdmin(true);
    try {
      await axios.post(`${API}/super-admin/admins`, newAdmin, { headers });
      toast.success(`Admin ${newAdmin.email} created`);
      setNewAdmin({ name: '', email: '', password: '' });
      setShowCreateAdmin(false);
      await loadAdmins();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create admin'); }
    finally { setCreatingAdmin(false); }
  };

  const handleToggleAdminStatus = async (adminId, currentActive) => {
    try {
      await axios.put(`${API}/super-admin/admins/${adminId}`, { is_active: !currentActive }, { headers });
      toast.success(`Admin ${currentActive ? 'deactivated' : 'reactivated'}`);
      await loadAdmins();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update admin'); }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwForm.new_password !== pwForm.confirm) { toast.error('Passwords do not match'); return; }
    setChangingPw(true);
    try {
      await axios.post(`${API}/super-admin/auth/change-password`, { current_password: pwForm.current_password, new_password: pwForm.new_password }, { headers });
      toast.success('Password changed successfully');
      setPwForm({ current_password: '', new_password: '', confirm: '' });
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to change password'); }
    finally { setChangingPw(false); }
  };

  const handleCreateOrg = async (e) => {
    e.preventDefault();
    setCreatingOrg(true);
    try {
      const res = await axios.post(`${API}/super-admin/orgs`, newOrg, { headers });
      toast.success(res.data.message);
      setNewOrg({ name: '', slug: '', plan: 'trial', max_users: 25, admin_name: '', admin_email: '', admin_password: '' });
      setShowCreateOrg(false);
      await Promise.all([loadOrgs(), loadDashboard()]);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create organization'); }
    finally { setCreatingOrg(false); }
  };

  const handleEditOrg = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/super-admin/orgs/${orgDetail.id}`, editOrgForm, { headers });
      toast.success('Organization updated');
      setShowEditOrg(false);
      await loadOrgs();
      await loadOrgDetail(orgDetail.id);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update org'); }
  };

  const handleDeleteOrg = async (orgId) => {
    try {
      await axios.delete(`${API}/super-admin/orgs/${orgId}`, { headers });
      toast.success('Organization deleted');
      setDeleteConfirm(null);
      setView('orgs');
      await Promise.all([loadOrgs(), loadDashboard()]);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete org'); }
  };

  const handleGlobalSearch = async (q) => {
    setGlobalSearch(q);
    if (q.length < 2) { setGlobalResults([]); return; }
    setSearching(true);
    try {
      const res = await axios.get(`${API}/super-admin/search/users?q=${encodeURIComponent(q)}`, { headers });
      setGlobalResults(res.data);
    } catch (err) { /* ignore */ }
    finally { setSearching(false); }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setAddingUser(true);
    try {
      await axios.post(`${API}/super-admin/orgs/${orgDetail.id}/users`, newUser, { headers });
      toast.success(`User ${newUser.email} added`);
      setNewUser({ name: '', email: '', password: '', role: 'tier1' });
      setShowAddUser(false);
      await loadOrgDetail(orgDetail.id);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add user'); }
    finally { setAddingUser(false); }
  };

  const handleUpdateUser = async (userId, updates) => {
    try {
      await axios.put(`${API}/super-admin/orgs/${orgDetail.id}/users/${userId}`, updates, { headers });
      toast.success('User updated');
      setUserAction(null);
      await loadOrgDetail(orgDetail.id);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update user'); }
  };

  const handleResetUserPw = async (userId) => {
    try {
      await axios.post(`${API}/super-admin/orgs/${orgDetail.id}/users/${userId}/reset-password`, { new_password: actionValue }, { headers });
      toast.success('Password reset');
      setUserAction(null);
      setActionValue('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to reset password'); }
  };

  const exportCSV = (url, filename) => {
    window.open(`${API}/super-admin/${url}?token=${token}`, '_blank');
    // Fallback: use fetch
    axios.get(`${API}/super-admin/${url}`, { headers, responseType: 'blob' }).then(res => {
      const blob = new Blob([res.data], { type: 'text/csv' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link.click();
    }).catch(() => toast.error('Export failed'));
  };

  const filteredOrgs = orgs.filter(o =>
    !search || o.name?.toLowerCase().includes(search.toLowerCase()) || o.slug?.toLowerCase().includes(search.toLowerCase())
  );

  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: Activity },
    { id: 'orgs', label: 'Organizations', icon: Building2 },
    { id: 'search', label: 'User Search', icon: Globe },
    { id: 'inspect', label: 'DB Inspector', icon: Eye, onClick: loadInspect },
    { id: 'admins', label: 'Admins', icon: ShieldCheck, onClick: () => loadAdmins() },
    { id: 'audit', label: 'Audit Log', icon: Eye, onClick: loadAuditLog },
    { id: 'settings', label: 'Settings', icon: Key },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-500" />
      </div>
    );
  }

  // Reusable inline input class
  const inputCls = "bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/30 outline-none";

  return (
    <div className="min-h-screen bg-gray-950 text-gray-100" data-testid="platform-dashboard">
      {/* Top Bar */}
      <header className="bg-gray-900 border-b border-gray-800 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield className="h-5 w-5 text-indigo-400" />
          <span className="text-sm font-semibold text-white">Shiftela Platform Admin</span>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-xs text-gray-400">{admin?.email}</span>
          <Button size="sm" variant="ghost" onClick={onLogout} className="text-gray-400 hover:text-white h-8" data-testid="platform-logout">
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <nav className="w-56 bg-gray-900/50 border-r border-gray-800 min-h-[calc(100vh-49px)] p-3 space-y-1">
          {navItems.map(item => (
            <button key={item.id} onClick={() => { setView(item.id); item.onClick?.(); }}
              className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                view === item.id || (view === 'org-detail' && item.id === 'orgs')
                  ? 'bg-indigo-600/20 text-indigo-300' : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`} data-testid={`nav-${item.id}`}>
              <item.icon className="h-4 w-4" />{item.label}
            </button>
          ))}
        </nav>

        {/* Main Content */}
        <main className="flex-1 p-6 overflow-y-auto max-h-[calc(100vh-49px)]">

          {/* ══ DASHBOARD ══════════════════════════════ */}
          {view === 'dashboard' && dashboard && (
            <div className="space-y-6" data-testid="dashboard-view">
              <h1 className="text-xl font-bold text-white">Platform Overview</h1>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {[
                  { label: 'Total Orgs', value: dashboard.total_orgs, icon: Building2, color: 'text-blue-400' },
                  { label: 'Active Orgs', value: dashboard.active_orgs, icon: Power, color: 'text-green-400' },
                  { label: 'Total Users', value: dashboard.total_users, icon: Users, color: 'text-purple-400' },
                  { label: 'Plans', value: Object.keys(dashboard.plan_breakdown || {}).length, icon: Crown, color: 'text-amber-400' },
                  { label: 'Platform Admins', value: dashboard.active_admins, icon: ShieldCheck, color: 'text-indigo-400' },
                ].map(stat => (
                  <div key={stat.label} className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <stat.icon className={`h-4 w-4 ${stat.color}`} />
                      <span className="text-xs text-gray-500">{stat.label}</span>
                    </div>
                    <p className="text-2xl font-bold text-white">{stat.value}</p>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Plan Distribution */}
                {dashboard.plan_breakdown && (
                  <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
                    <h3 className="text-sm font-medium text-gray-400 mb-3">Plan Distribution</h3>
                    <div className="flex gap-3 flex-wrap">
                      {Object.entries(dashboard.plan_breakdown).map(([plan, count]) => (
                        <div key={plan} className={`px-3 py-1.5 rounded-lg border text-sm font-medium ${PLAN_COLORS[plan] || PLAN_COLORS.free}`}>
                          {plan}: {count}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recent Activity */}
                <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
                  <h3 className="text-sm font-medium text-gray-400 mb-3">Recent Activity</h3>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {activity.length === 0 && <p className="text-xs text-gray-600">No recent activity</p>}
                    {activity.map((a, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs">
                        <span className="text-gray-500 w-24 shrink-0">{a.timestamp ? new Date(a.timestamp).toLocaleDateString() : ''}</span>
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                          a.type === 'login' ? 'bg-green-900/40 text-green-300' : 'bg-blue-900/40 text-blue-300'
                        }`}>{a.type}</span>
                        <span className="text-gray-300 truncate">{a.user}</span>
                        <span className="text-gray-600">in</span>
                        <span className="text-gray-400 truncate">{a.org_name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Org Usage Overview */}
              <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
                <h3 className="text-sm font-medium text-gray-400 mb-3">Organization Usage</h3>
                <div className="space-y-2">
                  {orgs.slice(0, 10).map(org => (
                    <div key={org.id} className="flex items-center gap-3 cursor-pointer hover:bg-gray-800/30 px-2 py-1.5 rounded-lg"
                      onClick={() => loadOrgDetail(org.id)}>
                      <span className="text-sm text-white w-40 truncate">{org.name}</span>
                      <div className="flex-1 bg-gray-800 rounded-full h-2 overflow-hidden">
                        <div className={`h-full rounded-full transition-all ${
                          (org.active_users / (org.max_users || 9999)) > 0.8 ? 'bg-red-500' :
                          (org.active_users / (org.max_users || 9999)) > 0.5 ? 'bg-yellow-500' : 'bg-indigo-500'
                        }`} style={{ width: `${Math.min(100, (org.active_users / (org.max_users || 9999)) * 100)}%` }} />
                      </div>
                      <span className="text-xs text-gray-400 w-20 text-right">{org.active_users} / {org.max_users || '?'}</span>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium border ${PLAN_COLORS[org.plan] || PLAN_COLORS.free}`}>{org.plan}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ══ ORGANIZATIONS ══════════════════════════ */}
          {view === 'orgs' && (
            <div className="space-y-4" data-testid="orgs-view">
              <div className="flex items-center justify-between">
                <h1 className="text-xl font-bold text-white">Organizations</h1>
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                    <input type="text" placeholder="Search orgs..." value={search} onChange={e => setSearch(e.target.value)}
                      className={`${inputCls} pl-9 w-64`} data-testid="org-search" />
                  </div>
                  <Button size="sm" onClick={() => setShowCreateOrg(true)} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="create-org-btn">
                    <Plus className="h-4 w-4 mr-1.5" /> New Org
                  </Button>
                </div>
              </div>

              {/* Create Org Form */}
              {showCreateOrg && (
                <div className="bg-gray-900 border border-gray-800 rounded-xl p-5" data-testid="create-org-form">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-medium text-white">Provision New Organization</h3>
                    <button onClick={() => setShowCreateOrg(false)} className="text-gray-500 hover:text-white"><X className="h-4 w-4" /></button>
                  </div>
                  <form onSubmit={handleCreateOrg} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Organization Name *</label>
                        <input type="text" placeholder="Acme Corp" value={newOrg.name} required
                          onChange={e => setNewOrg({ ...newOrg, name: e.target.value, slug: e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') })}
                          className={inputCls + ' w-full'} data-testid="new-org-name" />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Slug *</label>
                        <input type="text" placeholder="acme-corp" value={newOrg.slug} required minLength={3}
                          onChange={e => setNewOrg({ ...newOrg, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                          className={inputCls + ' w-full font-mono'} data-testid="new-org-slug" />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Plan</label>
                        <select value={newOrg.plan} onChange={e => setNewOrg({ ...newOrg, plan: e.target.value })} className={inputCls + ' w-full'} data-testid="new-org-plan">
                          <option value="trial">Trial</option><option value="free">Free</option><option value="pro">Pro</option><option value="business">Business</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Max Users</label>
                        <input type="number" min={1} value={newOrg.max_users} onChange={e => setNewOrg({ ...newOrg, max_users: parseInt(e.target.value) || 25 })}
                          className={inputCls + ' w-full'} data-testid="new-org-max-users" />
                      </div>
                    </div>
                    <div className="border-t border-gray-800 pt-3">
                      <p className="text-xs text-gray-500 mb-2">First Admin User (org owner)</p>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <input type="text" placeholder="Admin name" value={newOrg.admin_name} onChange={e => setNewOrg({ ...newOrg, admin_name: e.target.value })}
                          required className={inputCls + ' w-full'} data-testid="new-org-admin-name" />
                        <input type="email" placeholder="Admin email" value={newOrg.admin_email} onChange={e => setNewOrg({ ...newOrg, admin_email: e.target.value })}
                          required className={inputCls + ' w-full'} data-testid="new-org-admin-email" />
                        <input type="password" placeholder="Password (min 10 chars)" value={newOrg.admin_password} onChange={e => setNewOrg({ ...newOrg, admin_password: e.target.value })}
                          required minLength={10} className={inputCls + ' w-full'} data-testid="new-org-admin-password" />
                      </div>
                    </div>
                    <Button type="submit" size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white" disabled={creatingOrg} data-testid="submit-create-org">
                      {creatingOrg ? 'Provisioning...' : 'Create Organization'}
                    </Button>
                  </form>
                </div>
              )}

              <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-800">
                      <th className="text-left px-4 py-3 text-gray-500 font-medium">Organization</th>
                      <th className="text-left px-4 py-3 text-gray-500 font-medium">Plan</th>
                      <th className="text-left px-4 py-3 text-gray-500 font-medium">Usage</th>
                      <th className="text-center px-4 py-3 text-gray-500 font-medium">Status</th>
                      <th className="text-left px-4 py-3 text-gray-500 font-medium">Created</th>
                      <th className="w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOrgs.map(org => (
                      <tr key={org.id} className="border-b border-gray-800/50 hover:bg-gray-800/30 cursor-pointer"
                        onClick={() => loadOrgDetail(org.id)} data-testid={`org-row-${org.id}`}>
                        <td className="px-4 py-3">
                          <p className="font-medium text-white">{org.name}</p>
                          <p className="text-xs text-gray-500">{org.slug}</p>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium border ${PLAN_COLORS[org.plan] || PLAN_COLORS.free}`}>{org.plan}</span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-16 bg-gray-800 rounded-full h-1.5 overflow-hidden">
                              <div className={`h-full rounded-full ${(org.active_users / (org.max_users || 9999)) > 0.8 ? 'bg-red-500' : 'bg-indigo-500'}`}
                                style={{ width: `${Math.min(100, (org.active_users / (org.max_users || 9999)) * 100)}%` }} />
                            </div>
                            <span className="text-xs text-gray-400">{org.active_users}/{org.max_users || '?'}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`inline-flex items-center gap-1 text-xs font-medium ${org.is_active !== false ? 'text-green-400' : 'text-red-400'}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${org.is_active !== false ? 'bg-green-400' : 'bg-red-400'}`} />
                            {org.is_active !== false ? 'Active' : 'Suspended'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500">
                          {org.created_at ? new Date(org.created_at).toLocaleDateString() : '-'}
                        </td>
                        <td className="px-4 py-3"><ChevronRight className="h-4 w-4 text-gray-600" /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ══ ORG DETAIL ═════════════════════════════ */}
          {view === 'org-detail' && orgDetail && (
            <div className="space-y-6" data-testid="org-detail-view">
              {/* Breadcrumb + Actions */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <button onClick={() => setView('orgs')} className="text-gray-400 hover:text-white text-sm">Organizations</button>
                  <ChevronRight className="h-4 w-4 text-gray-600" />
                  <h1 className="text-xl font-bold text-white">{orgDetail.name}</h1>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium border ${PLAN_COLORS[orgDetail.plan] || PLAN_COLORS.free}`}>{orgDetail.plan}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={() => { setEditOrgForm({ name: orgDetail.name, slug: orgDetail.slug, max_users: orgDetail.max_users, plan: orgDetail.plan, owner_id: orgDetail.owner_id || '' }); setShowEditOrg(true); }}
                    className="border-gray-700 text-gray-300 hover:text-white h-8" data-testid="edit-org-btn">
                    <Edit2 className="h-3.5 w-3.5 mr-1" /> Edit
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => exportCSV(`orgs/${orgDetail.id}/users/export`, `${orgDetail.slug}-users.csv`)}
                    className="border-gray-700 text-gray-300 hover:text-white h-8" data-testid="export-users-btn">
                    <Download className="h-3.5 w-3.5 mr-1" /> Export
                  </Button>
                  {orgDetail.id !== 'default' && (
                    <Button size="sm" variant="destructive" onClick={() => setDeleteConfirm(orgDetail)} className="h-8" data-testid="delete-org-btn">
                      <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                    </Button>
                  )}
                </div>
              </div>

              {/* Edit Org Modal */}
              {showEditOrg && (
                <div className="bg-gray-900 border border-indigo-500/30 rounded-xl p-5" data-testid="edit-org-form">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-medium text-white">Edit Organization</h3>
                    <button onClick={() => setShowEditOrg(false)} className="text-gray-500 hover:text-white"><X className="h-4 w-4" /></button>
                  </div>
                  <form onSubmit={handleEditOrg} className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Name</label>
                      <input value={editOrgForm.name || ''} onChange={e => setEditOrgForm({ ...editOrgForm, name: e.target.value })} className={inputCls + ' w-full'} data-testid="edit-org-name" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Slug</label>
                      <input value={editOrgForm.slug || ''} onChange={e => setEditOrgForm({ ...editOrgForm, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })} className={inputCls + ' w-full font-mono'} data-testid="edit-org-slug" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Plan</label>
                      <select value={editOrgForm.plan || 'trial'} onChange={e => setEditOrgForm({ ...editOrgForm, plan: e.target.value })} className={inputCls + ' w-full'} data-testid="edit-org-plan">
                        <option value="trial">Trial</option><option value="free">Free</option><option value="pro">Pro</option><option value="business">Business</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Max Users</label>
                      <input type="number" min={1} value={editOrgForm.max_users || ''} onChange={e => setEditOrgForm({ ...editOrgForm, max_users: parseInt(e.target.value) || 25 })} className={inputCls + ' w-full'} data-testid="edit-org-max-users" />
                    </div>
                    <div className="col-span-full">
                      <label className="block text-xs text-gray-400 mb-1">Org Owner (admin fallback for notifications)</label>
                      <select value={editOrgForm.owner_id || ''} onChange={e => setEditOrgForm({ ...editOrgForm, owner_id: e.target.value })} className={inputCls + ' w-full'} data-testid="edit-org-owner">
                        <option value="">-- Select org owner --</option>
                        {orgUsers.filter(u => u.is_active).map(u => (
                          <option key={u.id} value={u.id}>{u.name || u.username || u.email} ({u.role}) — {u.email}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-span-full flex gap-2">
                      <Button type="submit" size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="save-org-btn">Save Changes</Button>
                      <Button type="button" size="sm" variant="ghost" onClick={() => setShowEditOrg(false)} className="text-gray-400">Cancel</Button>
                    </div>
                  </form>
                </div>
              )}

              {/* Org Info Cards */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {/* Owner Card */}
                <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 md:col-span-1">
                  <span className="text-xs text-gray-500">Org Owner</span>
                  {orgDetail.owner_info ? (
                    <div className="mt-1">
                      <p className="text-sm font-medium text-white">{orgDetail.owner_info.name}</p>
                      <p className="text-xs text-gray-400">{orgDetail.owner_info.email}</p>
                      <span className="inline-block mt-1 px-2 py-0.5 rounded bg-gray-800 border border-gray-700 text-[10px] text-gray-300">{orgDetail.owner_info.role}</span>
                    </div>
                  ) : (
                    <p className="text-sm text-amber-400 mt-1">Not set — <button className="underline hover:text-amber-300" onClick={() => { setEditOrgForm({ name: orgDetail.name, slug: orgDetail.slug, max_users: orgDetail.max_users, plan: orgDetail.plan, owner_id: '' }); setShowEditOrg(true); }}>assign now</button></p>
                  )}
                </div>
                {[
                  { label: 'Active Users', value: orgDetail.active_users },
                  { label: 'Max Users', value: orgDetail.max_users || 'Unlimited' },
                  { label: 'Slug', value: orgDetail.slug, mono: true },
                  { label: 'Database', value: orgDetail.db_name, mono: true },
                  { label: 'Created', value: orgDetail.created_at ? new Date(orgDetail.created_at).toLocaleDateString() : '-' },
                ].map(card => (
                  <div key={card.label} className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                    <span className="text-xs text-gray-500">{card.label}</span>
                    <p className={`text-lg font-bold text-white mt-1 ${card.mono ? 'text-sm font-mono text-gray-300' : ''}`}>{card.value}</p>
                  </div>
                ))}
              </div>

              {/* Quick Actions */}
              <div className="flex gap-2 flex-wrap">
                <Button size="sm" variant={orgDetail.is_active !== false ? 'destructive' : 'default'}
                  onClick={() => handleUpdateOrg(orgDetail.id, { is_active: orgDetail.is_active === false })} data-testid="toggle-org-status">
                  <Power className="h-4 w-4 mr-1" />{orgDetail.is_active !== false ? 'Suspend Org' : 'Reactivate Org'}
                </Button>
                {orgDetail.trial_ends_at && (
                  <span className="px-3 py-1.5 rounded-lg bg-yellow-900/30 border border-yellow-800/50 text-yellow-300 text-xs font-medium">
                    Trial expires: {new Date(orgDetail.trial_ends_at).toLocaleDateString()}
                  </span>
                )}
              </div>

              {/* Role Breakdown + Feature Toggles side by side */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {orgDetail.role_breakdown && Object.keys(orgDetail.role_breakdown).length > 0 && (
                  <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
                    <h3 className="text-sm font-medium text-gray-400 mb-3">Role Breakdown</h3>
                    <div className="flex gap-2 flex-wrap">
                      {Object.entries(orgDetail.role_breakdown).map(([role, count]) => (
                        <div key={role} className="bg-gray-800 border border-gray-700 px-3 py-1.5 rounded-lg text-sm">
                          <span className="text-gray-400">{role}:</span> <span className="text-white font-medium">{count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {orgDetail.features && Object.keys(orgDetail.features).length > 0 && (
                  <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
                    <h3 className="text-sm font-medium text-gray-400 mb-3">Feature Toggles</h3>
                    <div className="grid grid-cols-2 gap-2">
                      {Object.entries(orgDetail.features).map(([key, enabled]) => (
                        <button key={key} onClick={() => handleToggleFeature(orgDetail.id, key, orgDetail.features)}
                          className={`flex items-center justify-between px-3 py-2 rounded-lg border text-sm transition-colors ${
                            enabled ? 'bg-green-900/20 border-green-800/50 text-green-300' : 'bg-gray-800 border-gray-700 text-gray-500'
                          }`} data-testid={`feature-${key}`}>
                          <span>{key.replace(/_/g, ' ')}</span>
                          {enabled ? <ToggleRight className="h-4 w-4" /> : <ToggleLeft className="h-4 w-4" />}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Users Table */}
              <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
                <div className="px-5 py-3 border-b border-gray-800 flex items-center justify-between">
                  <h3 className="text-sm font-medium text-gray-400">Users ({orgUsers.length})</h3>
                  <Button size="sm" onClick={() => setShowAddUser(true)} className="bg-indigo-600 hover:bg-indigo-700 text-white h-7 text-xs" data-testid="add-user-btn">
                    <UserPlus className="h-3.5 w-3.5 mr-1" /> Add User
                  </Button>
                </div>

                {/* Add User Form */}
                {showAddUser && (
                  <div className="px-5 py-3 border-b border-gray-800 bg-gray-800/30" data-testid="add-user-form">
                    <form onSubmit={handleAddUser} className="flex items-end gap-3">
                      <div><label className="block text-xs text-gray-400 mb-1">Name</label><input value={newUser.name} onChange={e => setNewUser({ ...newUser, name: e.target.value })} required className={inputCls} placeholder="Name" data-testid="new-user-name" /></div>
                      <div><label className="block text-xs text-gray-400 mb-1">Email</label><input type="email" value={newUser.email} onChange={e => setNewUser({ ...newUser, email: e.target.value })} required className={inputCls} placeholder="Email" data-testid="new-user-email" /></div>
                      <div><label className="block text-xs text-gray-400 mb-1">Password</label><input type="password" value={newUser.password} onChange={e => setNewUser({ ...newUser, password: e.target.value })} required minLength={10} className={inputCls} placeholder="Min 10 chars" data-testid="new-user-password" /></div>
                      <div><label className="block text-xs text-gray-400 mb-1">Role</label>
                        <select value={newUser.role} onChange={e => setNewUser({ ...newUser, role: e.target.value })} className={inputCls} data-testid="new-user-role">
                          {getOrgRoles(orgUsers).map(r => <option key={r} value={r}>{r}</option>)}
                        </select>
                      </div>
                      <Button type="submit" size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white h-9" disabled={addingUser} data-testid="submit-add-user">{addingUser ? '...' : 'Add'}</Button>
                      <Button type="button" size="sm" variant="ghost" onClick={() => setShowAddUser(false)} className="text-gray-400 h-9"><X className="h-4 w-4" /></Button>
                    </form>
                  </div>
                )}

                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-800">
                      <th className="text-left px-4 py-2.5 text-gray-500 font-medium">Name</th>
                      <th className="text-left px-4 py-2.5 text-gray-500 font-medium">Email</th>
                      <th className="text-left px-4 py-2.5 text-gray-500 font-medium">Role</th>
                      <th className="text-center px-4 py-2.5 text-gray-500 font-medium">Status</th>
                      <th className="text-right px-4 py-2.5 text-gray-500 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orgUsers.map(u => (
                      <tr key={u.id} className="border-b border-gray-800/50 hover:bg-gray-800/30" data-testid={`user-row-${u.id}`}>
                        <td className="px-4 py-2.5 text-white">{u.name || u.username || '-'}</td>
                        <td className="px-4 py-2.5 text-gray-400">{u.email || '-'}</td>
                        <td className="px-4 py-2.5">
                          <select value={u.role} onChange={e => handleUpdateUser(u.id, { role: e.target.value })}
                            className="bg-transparent border border-gray-700 rounded px-2 py-0.5 text-xs text-gray-300 cursor-pointer hover:border-indigo-500"
                            data-testid={`role-select-${u.id}`}>
                            {getOrgRoles(orgUsers).map(r => <option key={r} value={r}>{r}</option>)}
                          </select>
                        </td>
                        <td className="px-4 py-2.5 text-center">
                          <button onClick={() => handleUpdateUser(u.id, { is_active: !u.is_active })}
                            className={`text-xs font-medium cursor-pointer ${u.is_active ? 'text-green-400 hover:text-red-400' : 'text-red-400 hover:text-green-400'}`}
                            title={u.is_active ? 'Click to deactivate' : 'Click to reactivate'} data-testid={`toggle-user-${u.id}`}>
                            {u.is_active ? 'Active' : 'Inactive'}
                          </button>
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button onClick={() => { setUserAction({ type: 'reset_password', user: u }); setActionValue(''); }}
                              className="text-xs text-gray-400 hover:text-amber-300 px-1.5 py-0.5 rounded hover:bg-gray-800" title="Reset password"
                              data-testid={`reset-pw-${u.id}`}>
                              <Key className="h-3.5 w-3.5" />
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); handleImpersonate(orgDetail.id, u.id); }}
                              className="text-xs text-indigo-400 hover:text-indigo-300 px-1.5 py-0.5 rounded hover:bg-gray-800" title="Login as this user"
                              data-testid={`impersonate-${u.id}`}>
                              <ExternalLink className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ══ GLOBAL USER SEARCH ═════════════════════ */}
          {view === 'search' && (
            <div className="space-y-4" data-testid="search-view">
              <h1 className="text-xl font-bold text-white">Global User Search</h1>
              <div className="relative max-w-xl">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                <input type="text" placeholder="Search by name or email across all orgs..." value={globalSearch}
                  onChange={e => handleGlobalSearch(e.target.value)} className={`${inputCls} pl-9 w-full`} data-testid="global-search-input" />
                {searching && <RefreshCw className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 animate-spin" />}
              </div>
              {globalResults.length > 0 && (
                <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-800">
                        <th className="text-left px-4 py-3 text-gray-500 font-medium">Name</th>
                        <th className="text-left px-4 py-3 text-gray-500 font-medium">Email</th>
                        <th className="text-left px-4 py-3 text-gray-500 font-medium">Organization</th>
                        <th className="text-left px-4 py-3 text-gray-500 font-medium">Role</th>
                        <th className="text-center px-4 py-3 text-gray-500 font-medium">Status</th>
                        <th className="text-right px-4 py-3 text-gray-500 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {globalResults.map((u, i) => (
                        <tr key={`${u.org_id}-${u.id}-${i}`} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                          <td className="px-4 py-2.5 text-white">{u.name || u.username || '-'}</td>
                          <td className="px-4 py-2.5 text-gray-400">{u.email}</td>
                          <td className="px-4 py-2.5">
                            <button className="text-indigo-400 hover:underline text-xs" onClick={() => loadOrgDetail(u.org_id)}>{u.org_name}</button>
                          </td>
                          <td className="px-4 py-2.5"><span className="px-2 py-0.5 rounded bg-gray-800 border border-gray-700 text-xs text-gray-300">{u.role}</span></td>
                          <td className="px-4 py-2.5 text-center"><span className={`text-xs ${u.is_active ? 'text-green-400' : 'text-red-400'}`}>{u.is_active ? 'Active' : 'Inactive'}</span></td>
                          <td className="px-4 py-2.5 text-right">
                            <button onClick={() => handleImpersonate(u.org_id, u.id)} className="text-xs text-indigo-400 hover:text-indigo-300" data-testid={`search-impersonate-${u.id}`}>
                              <ExternalLink className="h-3.5 w-3.5 inline mr-1" />Login as
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {globalSearch.length >= 2 && !searching && globalResults.length === 0 && (
                <p className="text-sm text-gray-500 text-center py-8">No users found matching "{globalSearch}"</p>
              )}
            </div>
          )}

          {/* ══ DB INSPECTOR ═════════════════════════════ */}
          {view === 'inspect' && (
            <div className="space-y-4" data-testid="inspect-view">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-xl font-bold text-white">Database Inspector</h1>
                  <p className="text-xs text-gray-400 mt-1">
                    Maps each organization to its underlying MongoDB. Flags orgs that share a database (org_id collisions, phantom orgs).
                  </p>
                </div>
                <Button size="sm" onClick={loadInspect} disabled={inspectLoading} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="inspect-refresh-btn">
                  {inspectLoading ? 'Scanning…' : 'Refresh Scan'}
                </Button>
              </div>

              {inspectData && (
                <>
                  {/* Shared-DB warning panel */}
                  {Object.keys(inspectData.shared_dbs_detected || {}).length > 0 ? (
                    <div className="bg-red-950/40 border border-red-700/60 rounded-xl p-4" data-testid="inspect-shared-warning">
                      <div className="flex items-start gap-3">
                        <span className="text-red-400 text-lg">⚠</span>
                        <div className="flex-1">
                          <h3 className="text-sm font-semibold text-red-300">Shared databases detected!</h3>
                          <p className="text-xs text-gray-300 mt-1">
                            Multiple organizations map to the same MongoDB database. Do NOT use the "Drop DB" delete option on these — it will destroy data for all orgs sharing the DB.
                          </p>
                          <div className="mt-2 space-y-1">
                            {Object.entries(inspectData.shared_dbs_detected).map(([db, ids]) => (
                              <div key={db} className="text-xs text-red-200 font-mono">
                                <span className="text-red-400">{db}</span> ← {ids.length} orgs: {ids.join(', ')}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-green-950/30 border border-green-800/50 rounded-xl p-3 text-xs text-green-300" data-testid="inspect-clean-status">
                      ✓ No shared databases detected. All {inspectData.total_orgs} orgs map to their own DB.
                    </div>
                  )}

                  {/* Per-org table */}
                  <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-800/60 text-gray-400 text-xs uppercase">
                        <tr>
                          <th className="text-left px-4 py-2">Org Name</th>
                          <th className="text-left px-4 py-2">Org ID</th>
                          <th className="text-left px-4 py-2">DB Name (stored)</th>
                          <th className="text-right px-4 py-2">Users</th>
                          <th className="text-right px-4 py-2">Active</th>
                          <th className="text-right px-4 py-2">Admins</th>
                          <th className="text-left px-4 py-2">Shared</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-800">
                        {(inspectData.orgs || []).map(o => (
                          <tr key={o.id} className={o.db_shared_with?.length ? 'bg-red-950/30' : 'hover:bg-gray-800/40'} data-testid={`inspect-row-${o.id}`}>
                            <td className="px-4 py-2 text-white">{o.name || <span className="text-gray-500 italic">(no name)</span>}</td>
                            <td className="px-4 py-2 text-gray-400 font-mono text-xs">{o.id}</td>
                            <td className="px-4 py-2 text-gray-400 font-mono text-xs">
                              {o.db_name}
                              {o.db_name_mismatch && (
                                <div className="text-amber-400 text-[10px] mt-0.5">
                                  ⚠ should be: {o.computed_db_name}
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-2 text-right text-gray-300">{o.user_count}</td>
                            <td className="px-4 py-2 text-right text-gray-300">{o.active_count}</td>
                            <td className="px-4 py-2 text-right text-gray-300">{o.admin_count}</td>
                            <td className="px-4 py-2 text-xs">
                              {o.db_shared_with?.length ? (
                                <span className="text-red-400 font-mono">⚠ {o.db_shared_with.join(', ')}</span>
                              ) : <span className="text-gray-600">—</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {!inspectData && !inspectLoading && (
                <div className="text-center py-8 text-gray-500 text-sm">Click "Refresh Scan" to inspect.</div>
              )}
            </div>
          )}

          {/* ══ ADMINS ═════════════════════════════════ */}
          {view === 'admins' && (
            <div className="space-y-4" data-testid="admins-view">
              <div className="flex items-center justify-between">
                <h1 className="text-xl font-bold text-white">Platform Admins</h1>
                <Button size="sm" onClick={() => setShowCreateAdmin(true)} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="create-admin-btn">
                  <UserPlus className="h-4 w-4 mr-1.5" /> Add Admin
                </Button>
              </div>
              {showCreateAdmin && (
                <div className="bg-gray-900 border border-gray-800 rounded-xl p-5" data-testid="create-admin-form">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-medium text-white">Create New Admin</h3>
                    <button onClick={() => setShowCreateAdmin(false)} className="text-gray-500 hover:text-white"><X className="h-4 w-4" /></button>
                  </div>
                  <form onSubmit={handleCreateAdmin} className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <input type="text" placeholder="Full name" value={newAdmin.name} onChange={e => setNewAdmin({ ...newAdmin, name: e.target.value })} required className={inputCls} data-testid="new-admin-name" />
                    <input type="email" placeholder="Email" value={newAdmin.email} onChange={e => setNewAdmin({ ...newAdmin, email: e.target.value })} required className={inputCls} data-testid="new-admin-email" />
                    <input type="password" placeholder="Password (min 10 chars)" value={newAdmin.password} onChange={e => setNewAdmin({ ...newAdmin, password: e.target.value })} required minLength={10} className={inputCls} data-testid="new-admin-password" />
                    <div className="md:col-span-3"><Button type="submit" size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white" disabled={creatingAdmin} data-testid="submit-create-admin">{creatingAdmin ? 'Creating...' : 'Create Admin'}</Button></div>
                  </form>
                </div>
              )}
              <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-gray-800">
                    <th className="text-left px-4 py-3 text-gray-500 font-medium">Name</th>
                    <th className="text-left px-4 py-3 text-gray-500 font-medium">Email</th>
                    <th className="text-center px-4 py-3 text-gray-500 font-medium">Status</th>
                    <th className="text-left px-4 py-3 text-gray-500 font-medium">Created</th>
                    <th className="text-center px-4 py-3 text-gray-500 font-medium">Actions</th>
                  </tr></thead>
                  <tbody>
                    {admins.map(a => (
                      <tr key={a.id} className="border-b border-gray-800/50 hover:bg-gray-800/30" data-testid={`admin-row-${a.id}`}>
                        <td className="px-4 py-3 text-white font-medium">{a.name}{a.id === admin.id && <span className="ml-2 text-xs text-indigo-400">(you)</span>}</td>
                        <td className="px-4 py-3 text-gray-400">{a.email}</td>
                        <td className="px-4 py-3 text-center">
                          <span className={`inline-flex items-center gap-1 text-xs font-medium ${a.is_active ? 'text-green-400' : 'text-red-400'}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${a.is_active ? 'bg-green-400' : 'bg-red-400'}`} />{a.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-500 text-xs">{a.created_at ? new Date(a.created_at).toLocaleDateString() : '-'}</td>
                        <td className="px-4 py-3 text-center">
                          {a.id !== admin.id && (
                            <Button size="sm" variant="ghost" className={`h-7 text-xs ${a.is_active ? 'text-red-400 hover:text-red-300' : 'text-green-400 hover:text-green-300'}`}
                              onClick={() => handleToggleAdminStatus(a.id, a.is_active)} data-testid={`toggle-admin-${a.id}`}>
                              {a.is_active ? 'Deactivate' : 'Reactivate'}
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ══ AUDIT LOG ══════════════════════════════ */}
          {view === 'audit' && (
            <div className="space-y-4" data-testid="audit-view">
              <div className="flex items-center justify-between">
                <h1 className="text-xl font-bold text-white">Admin Audit Log</h1>
                <Button size="sm" variant="outline" onClick={() => exportCSV('audit-log/export', 'platform-audit-log.csv')}
                  className="border-gray-700 text-gray-300 hover:text-white h-8" data-testid="export-audit-btn">
                  <Download className="h-3.5 w-3.5 mr-1" /> Export CSV
                </Button>
              </div>
              <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-gray-800">
                    <th className="text-left px-4 py-3 text-gray-500 font-medium">Time</th>
                    <th className="text-left px-4 py-3 text-gray-500 font-medium">Action</th>
                    <th className="text-left px-4 py-3 text-gray-500 font-medium">Details</th>
                  </tr></thead>
                  <tbody>
                    {auditLog.map(log => (
                      <tr key={log.id} className="border-b border-gray-800/50">
                        <td className="px-4 py-2.5 text-gray-400 text-xs whitespace-nowrap">{new Date(log.created_at).toLocaleString()}</td>
                        <td className="px-4 py-2.5">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            log.action === 'impersonate' ? 'bg-amber-900/40 text-amber-300' :
                            log.action === 'login' ? 'bg-green-900/40 text-green-300' :
                            log.action.includes('create') ? 'bg-indigo-900/40 text-indigo-300' :
                            log.action.includes('delete') ? 'bg-red-900/40 text-red-300' :
                            log.action.includes('password') ? 'bg-orange-900/40 text-orange-300' :
                            'bg-blue-900/40 text-blue-300'
                          }`}>{log.action.replace(/_/g, ' ')}</span>
                        </td>
                        <td className="px-4 py-2.5 text-gray-400 text-xs">
                          {log.target_org_id && <span>Org: {log.target_org_id} </span>}
                          {log.target_user_email && <span>User: {log.target_user_email} </span>}
                          {log.target_user_role && <span>({log.target_user_role}) </span>}
                          {log.details && typeof log.details === 'object' && Object.entries(log.details).map(([k, v]) => (
                            <span key={k} className="mr-2">{k}: {typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
                          ))}
                          {log.changes && <span>{JSON.stringify(log.changes)}</span>}
                        </td>
                      </tr>
                    ))}
                    {auditLog.length === 0 && <tr><td colSpan={3} className="text-center py-8 text-gray-500">No audit entries</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ══ SETTINGS ═══════════════════════════════ */}
          {view === 'settings' && (
            <div className="space-y-6 max-w-lg" data-testid="settings-view">
              <h1 className="text-xl font-bold text-white">Account Settings</h1>
              <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-4">
                  <Lock className="h-4 w-4 text-indigo-400" />
                  <h3 className="text-sm font-medium text-white">Change Password</h3>
                </div>
                <form onSubmit={handleChangePassword} className="space-y-3">
                  <div><label className="block text-xs text-gray-400 mb-1">Current Password</label><input type="password" value={pwForm.current_password} onChange={e => setPwForm({ ...pwForm, current_password: e.target.value })} required className={inputCls + ' w-full'} data-testid="current-password" /></div>
                  <div><label className="block text-xs text-gray-400 mb-1">New Password</label><input type="password" value={pwForm.new_password} onChange={e => setPwForm({ ...pwForm, new_password: e.target.value })} required minLength={10} className={inputCls + ' w-full'} data-testid="new-password" /></div>
                  <div><label className="block text-xs text-gray-400 mb-1">Confirm</label><input type="password" value={pwForm.confirm} onChange={e => setPwForm({ ...pwForm, confirm: e.target.value })} required className={`${inputCls} w-full ${pwForm.confirm && pwForm.confirm !== pwForm.new_password ? 'border-red-600' : ''}`} data-testid="confirm-password" /></div>
                  {pwForm.confirm && pwForm.confirm !== pwForm.new_password && <p className="text-xs text-red-400">Passwords do not match</p>}
                  <Button type="submit" size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white" disabled={changingPw} data-testid="change-password-btn">{changingPw ? 'Updating...' : 'Update Password'}</Button>
                </form>
              </div>
              <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-2"><Shield className="h-4 w-4 text-indigo-400" /><h3 className="text-sm font-medium text-white">Security Info</h3></div>
                <div className="space-y-2 text-sm text-gray-400">
                  <p>Sessions expire after <span className="text-white font-medium">4 hours</span></p>
                  <p>Login locked after <span className="text-white font-medium">5 failed attempts</span> for 15 minutes</p>
                  <p>All actions are recorded in the <button onClick={loadAuditLog} className="text-indigo-400 hover:underline">audit log</button></p>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>

      {/* ══ MODALS ═════════════════════════════════════ */}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" data-testid="delete-confirm-modal">
          <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-bold text-white mb-2">Delete Organization</h3>
            <p className="text-sm text-gray-400 mb-4">
              This will permanently delete <span className="text-white font-medium">{deleteConfirm.name}</span> and drop its database.
              This action cannot be undone.
            </p>
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" onClick={() => setDeleteConfirm(null)} className="text-gray-400">Cancel</Button>
              <Button variant="destructive" onClick={() => handleDeleteOrg(deleteConfirm.id)} data-testid="confirm-delete-org">Delete Organization</Button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {userAction?.type === 'reset_password' && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" data-testid="reset-password-modal">
          <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-bold text-white mb-2">Reset Password</h3>
            <p className="text-sm text-gray-400 mb-4">Set a new password for <span className="text-white font-medium">{userAction.user.email}</span></p>
            <input type="password" placeholder="New password (min 10 chars)" value={actionValue} onChange={e => setActionValue(e.target.value)}
              className={inputCls + ' w-full mb-3'} minLength={10} data-testid="reset-password-input" />
            <p className="text-xs text-gray-500 mb-4">Must have: 10+ chars, uppercase, lowercase, number, special character. User will be prompted to change on next login.</p>
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" onClick={() => { setUserAction(null); setActionValue(''); }} className="text-gray-400">Cancel</Button>
              <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => handleResetUserPw(userAction.user.id)} disabled={actionValue.length < 10} data-testid="confirm-reset-password">Reset Password</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
